import { Howl } from 'howler';


/**
 * A class to manage game sounds and music using the Howler.js library.
 */
export default class Sounds {
    /**
     * Initializes sound effects, music playlist, and configuration options.
     */
    constructor() {

        /** @private {string} Base URL for audio files */
        this.base = `${window.location.origin}/_resources/themes/mercury/dist/audio/`;

        /** @private {number} Timestamp of the last bounce sound */
        this.last_bounce = 0;

        /** @private {number} Minimum time (ms) between bounce sounds */
        this.bounce_cooldown = 400;

        /** @private {number} Index of the current music track */
        this.current_index = Math.floor(Math.random() * 4);

        /** @private {Howl|null} The currently playing music track */
        this.playlist_howl = null;

        /** @private {Object<string, Howl>} Preloaded sound effects */
        this.sounds = this.createSounds([
            ['kick', 0.5],
            ['point', 0.3],
            ['bounce1', 0.4],
            ['bounce2', 0.4],
            ['whistle', 0.4],
            ['woosh_in', 0.05],
            ['metal_pole', 0.05],
            ['negative', 0.4],
            ['whoosh', 0.4],
            ['coin', 0.4],
            ['sparkle', 0.4],
        ]);

        /** @private {string[]} Music playlist */
        this.playlist = Array.from({ length: 4 }, (_, i) => `${this.base}music${i + 1}.mp3`);
    }

    /**
     * Creates Howl sound objects from configuration.
     * @private
     * @param {[string, number][]} configs - Array of [name, volume] pairs.
     * @returns {Object<string, Howl>} Object mapping names to Howl instances.
     */
    createSounds(configs) {
        return Object.fromEntries(configs.map(([name, volume]) => [
            name,
            new Howl({ src: [`${this.base}${name}.mp3`], html5: true, volume })
        ]));
    }

    /**
     * Plays a sound by name. Stops it first if it's already playing.
     * @param {string} name - The name of the sound to play.
     */
    play(name) {
        const sound = this.sounds[name];
        if (sound) {
            sound.stop();
            sound.play();
        }
    }

    /**
     * Plays a sound at a specified volume.
     * @param {string} name - The name of the sound to play.
     * @param {number} volume - The volume level (0.0 to 1.0).
     */
    playAtVolume(name, volume) {
        const sound = this.sounds[name];
        if (sound) {
            sound.volume(volume);
            sound.stop();
            sound.play();
        }
    }

    /**
     * Sets the volume for a specific sound.
     * @param {string} name - The name of the sound.
     * @param {number} volume - The volume level (0.0 to 1.0).
     */
    setVolume(name, volume) {
        this.sounds[name]?.volume(volume);
    }

    /**
     * Stops all currently playing sound effects.
     */
    stop() {
        Object.values(this.sounds).forEach(sound => sound.stop());
    }

    /**
     * Mutes or unmutes all sound effects.
     * @param {boolean} [state=true] - Whether to mute (`true`) or unmute (`false`).
     */
    mute(state = true) {
        Howler.mute(state);
    }

    /** 
     * Unmutes all sound effects.
     */
    unmute() {
        this.mute(false);
    }

    /**
     * Plays a bounce sound with volume based on impact velocity and a cooldown.
     * @param {number} now - Current timestamp (e.g. from performance.now()).
     * @param {number} impact_velocity - Velocity of the bounce.
     */
    bounce(now, impact_velocity) {
        const canPlay = impact_velocity > 0.1 && now - this.last_bounce >= this.bounce_cooldown;
        if (!canPlay) return;

        const volume = Math.min(impact_velocity / 4, 1) / 4;
        const bounceSound = Math.random() < 0.5 ? 'bounce1' : 'bounce2';

        this.playAtVolume(bounceSound, volume);
        this.last_bounce = now;
    }

    /**
     * Plays the next music track in the playlist. Automatically loops.
     */
    playNextTrack() {
        if (this.playlist_howl) {
            this.playlist_howl.unload();
        }

        const nextTrack = this.playlist[this.current_index];
        this.playlist_howl = new Howl({
            src: [nextTrack],
            html5: true,
            volume: 0.05,
            onend: () => {
                this.current_index = (this.current_index + 1) % this.playlist.length;
                this.playNextTrack();
            }
        });

        this.playlist_howl.play();
    }
}